import * as THREE from "three";
import fetchProgress from "fetch-progress";
import {MODE_TYPE} from "utils/constants";

export async function _playModeFetchFromS3(context, url) {
  const self = context;
  await fetch(url)
    .then(
      fetchProgress({
        onProgress(progress) {
          if (progress.remaining === 0) {
            // console.log('remaining is 0');
          } else {
            self.dispatchEvent(
              new CustomEvent("LOADING_START", {
                detail: {object: {processName: "SCENE_FETCH"}},
              }),
            );
            self.dispatchEvent(
              new CustomEvent("LOADING_IN_PROGRESS", {
                detail: {
                  object: {
                    progress: progress.percentage,
                    processName: "SCENE_FETCH",
                  },
                },
              }),
            );
          }
        },
      }),
    )
    .then(response => response.text())
    .then(responseJson => {
      let parsedScene = self.parseJSON(responseJson);
      context.versionManager.setVersion(parsedScene.version);

      let sceneDefault = parsedScene.default !== undefined ? parsedScene.default : parsedScene;
      // TEMP: Error occurs when texture image field is undefined
      sceneDefault.textures = sceneDefault.textures.filter(texture => {
        return texture.image !== undefined;
      });
      new THREE.ObjectLoader().parse(sceneDefault, scene => {
        if (!scene.userData.postProcessing) {
          scene.userData.postProcessing = {
            ssao: {},
            bloom: {},
            godrays: {},
            hueSaturation: {},
            brightnessContrast: {},
          };
        }
        if (!scene.userData.toneMapping) {
          scene.userData.toneMapping = {
            mode: THREE.NoToneMapping,
            exposure: 1,
          };
        }
        self.scenePlay = scene;
        self.scenePlay.traverse(child => {
          if (child.userData.script === undefined) {
            child.userData.script = {};
          }
          switch (child.userData.type) {
            case "asset":
              if (!child.userData.isMassless) self.assetObjects[child.uuid] = child;
              break;
            case "mesh":
              if (!child.userData.isMassless) {
                self.assetObjects[child.uuid] = child;
              }
              break;
            default:
              break;
          }
          if (child.name === "StartLocation") {
            self.startLocation = child;
          }
        });
        if (parsedScene.GUI !== undefined) {
          new THREE.ObjectLoader().parse(parsedScene.GUI, scene => {
            self.resetGUIScene(scene);
          });
        }
        self.updateCodeEditorMode();
        self.changeMode(MODE_TYPE.PLAY_MODE);
      });
    })
    .catch(error => {
      console.error(error);
    });
}

export async function _playModeFetchFromLocal(context, json) {
  const self = context;
  let parsedScene = json;
  context.versionManager.setVersion(parsedScene.version);

  let sceneDefault = parsedScene.default !== undefined ? parsedScene.default : parsedScene;
  // TEMP: Error occurs when texture image field is undefined
  sceneDefault.textures = sceneDefault.textures.filter(texture => {
    return texture.image !== undefined;
  });
  new THREE.ObjectLoader().parse(sceneDefault, scene => {
    if (!scene.userData.postProcessing) {
      scene.userData.postProcessing = {
        ssao: {},
        bloom: {},
        godrays: {},
        hueSaturation: {},
        brightnessContrast: {},
      };
    }
    if (!scene.userData.toneMapping) {
      scene.userData.toneMapping = {
        mode: THREE.NoToneMapping,
        exposure: 1,
      };
    }
    self.scenePlay = scene;
    self.scenePlay.traverse(child => {
      if (child.userData.script === undefined) {
        child.userData.script = {};
      }
      switch (child.userData.type) {
        case "asset":
          if (!child.userData.isMassless) self.assetObjects[child.uuid] = child;
          break;
        case "mesh":
          if (!child.userData.isMassless) {
            self.assetObjects[child.uuid] = child;
          }
          break;
        default:
          break;
      }
      if (child.name === "StartLocation") {
        self.startLocation = child;
      }
    });
    if (parsedScene.GUI !== undefined) {
      new THREE.ObjectLoader().parse(parsedScene.GUI, scene => {
        self.resetGUIScene(scene);
      });
    }
    self.updateCodeEditorMode();
    self.changeMode(MODE_TYPE.PLAY_MODE);
  });
}

export async function _editModeFetchFromS3(context, url) {
  const self = context;
  const isNewTextcodingProject = !!self.template && self.devLang === "text";
  await fetch(url)
    .then(
      fetchProgress({
        onProgress(progress) {
          if (progress.remaining === 0) {
            // console.log('remaining is 0');
          } else {
            self.dispatchEvent(
              new CustomEvent("LOADING_START", {
                detail: {object: {processName: "SAVE_SCENE_FETCH"}},
              }),
            );
            self.dispatchEvent(
              new CustomEvent("LOADING_IN_PROGRESS", {
                detail: {
                  object: {
                    progress: progress.percentage,
                    processName: "SAVE_SCENE_FETCH",
                  },
                },
              }),
            );
          }
        },
      }),
    )
    .then(response => response.text())
    .then(responseJson => {
      let parsedScene = self.parseJSON(responseJson);
      self.versionManager.setVersion(isNewTextcodingProject ? "LATEST" : parsedScene.version);
      // Projects that have already been published may cause an error
      if (parsedScene.GUI !== undefined) {
        new THREE.ObjectLoader().parse(parsedScene.GUI, scene => {
          self.resetGUIScene(scene);
        });
      }
      let sceneDefault = parsedScene.default !== undefined ? parsedScene.default : parsedScene;
      // TEMP: Error occurs when texture image field is undefined
      sceneDefault.textures = sceneDefault.textures.filter(texture => {
        return texture.image !== undefined;
      });
      new THREE.ObjectLoader().parse(sceneDefault, scene => {
        if (!scene.userData.postProcessing) {
          scene.userData.postProcessing = {
            ssao: {},
            bloom: {},
            godrays: {},
            hueSaturation: {},
            brightnessContrast: {},
          };
        }
        if (!scene.userData.toneMapping) {
          scene.userData.toneMapping = {
            mode: THREE.NoToneMapping,
            exposure: 1,
          };
        }
        self.resetEditScene(scene);
        if (isNewTextcodingProject) self.setupLatestVersion();
        self.dispatchEvent(new CustomEvent("LOADING_END", {detail: {object: undefined}}));
        self.changeMode(MODE_TYPE.EDIT_MODE);
      });
    })
    .catch(error => {
      console.error(error);
    });
}

export async function _editModeFetchFromS3CopyPID(context, primaryUrl, alternativeUrl) {
  const self = context;
  const isNewTextcodingProject = !!self.template && self.devLang === "text";

  async function tryFetch(url) {
    try {
      const response = await fetch(url);
      if (!response.ok) throw new Error("Fetch failed");
      return response;
    } catch (error) {
      console.error(`Fetch failed for ${url}:`, error);
      if (url === primaryUrl) {
        console.log(`Trying alternative URL: ${alternativeUrl}`);
        return tryFetch(alternativeUrl); // Attempt to fetch from the alternative URL
      }
      throw new Error(`Both primary and alternative fetch attempts failed`);
    }
  }

  tryFetch(primaryUrl)
    .then(
      fetchProgress({
        onProgress(progress) {
          if (progress.remaining === 0) {
            // console.log('remaining is 0');
          } else {
            self.dispatchEvent(
              new CustomEvent("LOADING_START", {
                detail: {object: {processName: "SAVE_SCENE_FETCH"}},
              }),
            );
            self.dispatchEvent(
              new CustomEvent("LOADING_IN_PROGRESS", {
                detail: {
                  object: {
                    progress: progress.percentage,
                    processName: "SAVE_SCENE_FETCH",
                  },
                },
              }),
            );
          }
        },
      }),
    )
    .then(response => response.text())
    .then(responseJson => {
      let parsedScene = self.parseJSON(responseJson);
      self.versionManager.setVersion(isNewTextcodingProject ? "LATEST" : parsedScene.version);
      if (parsedScene.GUI !== undefined) {
        new THREE.ObjectLoader().parse(parsedScene.GUI, scene => {
          self.resetGUIScene(scene);
        });
      }
      let sceneDefault = parsedScene.default !== undefined ? parsedScene.default : parsedScene;
      sceneDefault.textures = sceneDefault.textures.filter(texture => {
        return texture.image !== undefined;
      });
      new THREE.ObjectLoader().parse(sceneDefault, scene => {
        if (!scene.userData.postProcessing) {
          scene.userData.postProcessing = {};
        }
        if (!scene.userData.toneMapping) {
          scene.userData.toneMapping = {
            mode: THREE.NoToneMapping,
            exposure: 1,
          };
        }
        self.resetEditScene(scene);
        if (isNewTextcodingProject) self.setupLatestVersion();
        self.dispatchEvent(new CustomEvent("LOADING_END", {detail: {object: undefined}}));
        self.changeMode(MODE_TYPE.EDIT_MODE);
      });
    })
    .catch(error => {
      console.error("Final error handling:", error);
    });
}
